<template>
  <v-container class="full-height">
    <div v-if="!loading" class="d-flex justify-space-between pb-4">
      <v-btn class="third white--text" @click="openDialog"> {{ dateStr }}</v-btn>
      <v-dialog v-model="dialog" class="pa-0" width="290">
        <v-card class="d-flex flex-column align-end pa-0">
          <v-card-text class="d-flex justify-center pa-0">
            <v-date-picker
              v-model="picker"
              :show-week="true"
              :first-day-of-week="1"
              color="third"
            />
          </v-card-text>
          <v-card-actions class="flex-grow-1 justify-end">
            <v-btn class="third--text" text @click="closeDialog">Cancel</v-btn>
            <v-btn class="third--text" text @click="pickDate">Ok</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-btn color="third" class="white--text" @click="openFallModal">
        Nouvelle Chute
      </v-btn>
    </div>
    <div v-if="!loading" class="d-flex pb-4">
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        class="pb-4 pt-0 mt-0"
        color="third"
        @click.stop
      />
    </div>
    <v-data-table
      v-if="!loading"
      :headers="headers"
      :hide-default-footer="true"
      :items-per-page="-1"
      :items="falls"
      item-key="uuid"
      :search="search"
    >
      <template #item.actions="{ item }">
        <v-btn icon class="green" @click="handleEditFall(item)">
          <v-icon color="white">mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon class="red ml-2" @click="handleDeleteFall(item)">
          <v-icon color="white">mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <div v-else class="d-flex justify-center align-center full-height">
      <Loader />
    </div>

    <ConfirmationModal style="z-index: 100000;" title="Supprimer le chute" :activator.sync="showConfirmationModal" :close-modal="closeConfirmationModal" :action="deleteCustomerFall">
      <template #body>
        <h3 class="black--text">
          Êtes-vous sûr de vouloir supprimer le chute
        </h3>
      </template>
    </ConfirmationModal>
    <FallModal v-if="createEditModal" @addEditFall="addEditFall" />
  </v-container>
</template>

<script>
import { onBeforeMount, ref, provide, computed, watch } from '@vue/composition-api';

import { format, fromUnixTime, startOfWeek, endOfWeek, getISOWeek, getUnixTime } from 'date-fns';
import { fr } from 'date-fns/esm/locale';
import { getWorkspaceFalls, deleteFall, getCustomerFall } from '@/helpers/request';
import { getSessionById } from '@/helpers/amplify';

import Loader from '@/components/common/Loader.vue';
import ConfirmationModal from '@/components/common/ConfirmationModal.vue';
import FallModal from '@/components/fall/FallModal.vue';

import store from '@/store';

import { sendNotification } from '@/helpers/utils';

export default {
  components: { Loader, ConfirmationModal, FallModal },
  props: {
    customer: {
      type: Object,
      default: null
    }
  },
  setup (props) {
    const loading = ref(false);
    const falls = ref([]);
    const selectedFall = ref(null);
    const createEditModal = ref(false);
    const isEdit = ref(false);
    const search = ref(null);
    const selectParticipant = ref(null);

    const showConfirmationModal = ref(false);

    const dialog = ref(false);
    const picker = ref(format(new Date(), 'yyyy-MM-dd'));
    const selectedDate = ref(picker.value);

    const dateStr = computed(() => {
      const startWeek = format(startOfWeek(new Date(selectedDate.value), { weekStartsOn: 1 }), 'dd/MM/yyyy');
      const endWeek = format(endOfWeek(new Date(selectedDate.value), { weekStartsOn: 1 }), 'dd/MM/yyyy');
      const week = getISOWeek(new Date(selectedDate.value));
      return `[${week}] ${startWeek} - ${endWeek}`;
    });

    const originalFall = ref({
      uuid: null,
      session: null,
      ridder: null,
      horse: null,
      description: null,
      firefighter: false,
      time: null
    });

    const headers = [
      { text: 'session', value: 'sessionLabel' },
      { text: 'Jour', value: 'dateLabel' },
      { text: 'Heure', value: 'hourLabel' },
      { text: 'Nom du cavalier', value: 'fullname' },
      { text: 'Nom du cheval', value: 'horseName' },
      { text: 'circonstance/faits', value: 'description' },
      { text: 'Intervention pompier', value: 'firefighter' },
      { text: 'Actions', value: 'actions' }
    ];

    const prepareFall = fall => {
      const horse = store.getters.horse(fall.horse);
      return {
        sessionLabel: fall.session.name,
        dateLabel: format(fromUnixTime(fall.date), 'PPPP', { locale: fr }),
        hourLabel: format(fromUnixTime(fall.date), 'HH:mm', { locale: fr }),
        date: fall.date,
        fullname: fall.ridder.name,
        customerUuid: fall.ridder.uuid,
        uuid: fall.uuid,
        horseName: horse ? horse.horse_name : '--',
        description: fall.description,
        firefighter: fall.firefighter ? 'Oui' : 'Non',
        firefighterValue: fall.firefighter,
        session: fall.session
      };
    };

    const getFalls = async () => {
      loading.value = true;

      let response = null;

      if (props && props.customer) {
        response = await getCustomerFall(props.customer.uuid);
      } else {
        const start = getUnixTime(startOfWeek(new Date(selectedDate.value)));
        const end = getUnixTime(endOfWeek(new Date(selectedDate.value)));

        response = await getWorkspaceFalls(parseFloat(start), parseFloat(end));
      }

      loading.value = false;

      if (response.success) {
        falls.value = response.data.map(fall => prepareFall(fall));
      }
    };

    onBeforeMount(async () => {
      await getFalls();
    });

    const closeConfirmationModal = () => {
      showConfirmationModal.value = false;
    };

    const handleDeleteFall = item => {
      selectedFall.value = item;
      showConfirmationModal.value = true;
    };

    const handleEditFall = async item => {
      // selectedFall.value = item;
      const session = await getSessionById(item.session.id);

      const sessionParticipant = (session.participants || []).find(p => p.uuid === item.customerUuid);

      selectedFall.value = {
        uuid: item.uuid,
        session: session,
        ridder: sessionParticipant,
        horse: sessionParticipant ? sessionParticipant.horse : null,
        description: item.description,
        firefighter: item.firefighterValue,
        time: format(fromUnixTime(item.date), 'HH:mm')
      };
      isEdit.value = true;
      createEditModal.value = true;
    };

    const deleteCustomerFall = async () => {
      if (!selectedFall.value) {
        return;
      }
      showConfirmationModal.value = false;
      loading.value = true;

      let notification = {
        type: 'error',
        message: 'Erreur lors de la suppression de chute'
      };

      const response = await deleteFall(selectedFall.value.customerUuid, store.state.workspace.code, selectedFall.value.uuid);

      loading.value = false;

      if (response && response.success) {
        notification = {
          type: 'success',
          message: 'Chute supprimée avec succès'
        };
        const index = falls.value.findIndex(f => f.uuid === selectedFall.value.uuid);

        if (index !== -1) {
          falls.value.splice(index, 1);
        }
      }

      sendNotification(notification.type, notification.message);
    };

    const openFallModal = () => {
      selectParticipant.value = props.customer;
      selectedFall.value = { ...originalFall.value };
      isEdit.value = false;
      createEditModal.value = true;
    };

    const addEditFall = fall => {
      if (fall.create) {
        falls.value.push(prepareFall(fall));
        return;
      }
      // edit case
      const fallIndex = falls.value.findIndex(f => f.uuid === fall.uuid);

      if (fallIndex !== -1) {
        falls.value[fallIndex] = {
          ...falls.value[fallIndex],
          description: fall.description,
          dateLabel: format(fromUnixTime(fall.date), 'PPPP', { locale: fr }),
          hourLabel: format(fromUnixTime(fall.date), 'HH:mm', { locale: fr }),
          time: format(fromUnixTime(fall.date), 'HH:mm'),
          firefighter: fall.firefighter ? 'Oui' : 'Non',
          firefighterValue: fall.firefighter
        };
      }
    };

    const openDialog = () => {
      dialog.value = true;
    };

    const closeDialog = () => {
      dialog.value = false;
    };

    const pickDate = () => {
      selectedDate.value = picker.value;
      dialog.value = false;
    };

    watch(selectedDate, async () => {
      await getFalls();
    });

    provide('createEditModal', createEditModal);
    provide('isEdit', isEdit);
    provide('fallLoading', loading);
    provide('selectedFall', selectedFall);
    provide('originalFall', originalFall);
    provide('selectParticipant', selectParticipant);

    return { pickDate, dialog, openDialog, closeDialog, dateStr, selectedDate, picker, handleEditFall, addEditFall, createEditModal, headers, loading, falls, deleteCustomerFall, showConfirmationModal, closeConfirmationModal, handleDeleteFall, openFallModal, search };
  }
};
</script>
